import type { MouseEvent } from "react";

/**
 * Same fix as chat-ui's Composer (see its handleInputMouseDown): on iOS, the
 * browser's own focus handling runs a synchronous "will this element still
 * be visible once the keyboard is up?" check, and when it decides no, it
 * pans the visual viewport to reveal the element — dragging the whole
 * fixed-position app with it (see useAppViewport's header comment), which is
 * what was hiding these onboarding fields under the keyboard.
 *
 * preventDefault suppresses that default focus handling; the explicit
 * `focus({ preventScroll: true })` opens the keyboard without the check ever
 * running. Limited to coarse-pointer devices, same trade-off as Composer:
 * the cost is that a tap no longer positions the caret, which is a fair
 * trade for these short, freshly-typed onboarding fields, and a mouse keeps
 * its normal behaviour.
 *
 * `preventScroll` also suppresses the panel scroll iOS would have done to
 * bring the field into view, and on this screen that is wanted rather than
 * tolerated: onboarding's layout stays exactly as it was while the keyboard
 * is up (see useStaticAppHeight), so there is no shrink for a field to be
 * scrolled out of, and a scroll would itself be the movement this screen
 * exists to avoid. An earlier round paired this with a hook that scrolled the
 * focused field back into view; that pairing is gone.
 */
export function focusWithoutPan(event: MouseEvent<HTMLInputElement | HTMLTextAreaElement>): void {
  if (!window.matchMedia?.("(pointer: coarse)").matches) {
    return;
  }
  event.currentTarget.focus({ preventScroll: true });
  event.preventDefault();
}
